import React, {
  useState, useRef, useCallback, useEffect,
} from "react";
import { render } from "react-dom";
import useLanguageProvider from "components/LanguageProvider/hooks/useLanguageProvider";
import { client } from "helpers/apolloClientLink";
import { ApolloProvider, useMutation } from "@apollo/client";
import TurnstileWrapper from "components/TurnstileWrapper";
import { TURNSTILE_KEY } from "helpers/constants";
import ReportCampaignForm from "./components/ReportCampaignForm";
import REPORT_CAMPAIGN from "./queries/reportCampaign.gql";

import "./style.scss";
import ModalPinVerification from "../../components/ModalPinVerification";

const ReportCampaign = () => {
  const { translate: t } = useLanguageProvider();
  const [error, setError] = useState();
  const [submitted, setSubmitted] = useState(false);
  const [submittedFormData, setSubmittedFormData] = useState();
  const [isVerificationModalOpen, setVerificationModalOpen] = useState(false);

  const [captchaToken, setCaptchaToken] = useState();
  const [isTurnstileInteractive, setIsTurnstileInteractive] = useState();
  const [turnstileError, setTurnstileError] = useState();
  const turnstileRef = useRef(null);

  const refreshTurnstile = useCallback(() => {
    turnstileRef.current?.reset?.();
  }, []);

  const { campaignId, campaignUrl } = window.Chuffed.reportCampaignInit;

  const [submitReport, { loading }] = useMutation(REPORT_CAMPAIGN, {
    errorPolicy: "all",
    onCompleted(data) {
      if (data.reportCampaign?.submitted) {
        setSubmitted(true);
      } else {
        setError(data.reportCampaign?.message || "Unable to report campaign. Please try again later.");
      }
    },
    onError(error) {
      const errorMessage = error?.message ?? "Unable to report campaign. Please try again later.";
      setError(errorMessage);
      refreshTurnstile();
    },
  });

  const initialCaptureToken = useRef(null);
  // set initial token or check if captcha token has refreshed
  useEffect(() => {
    if (
      captchaToken
      && (initialCaptureToken.current === null
        || initialCaptureToken.current !== captchaToken
        || (captchaToken === "XXXX.DUMMY.TOKEN.XXXX" && process.env.NODE_ENV !== "production"))
    ) {
      initialCaptureToken.current = captchaToken;
    }
  }, [captchaToken]);

  const formContent = [
    {
      title: t("Report a Campaign"),
      fields: [
        {
          type: "text",
          name: "complainantName",
          label: t("Your Name"),
          classes: "",
          required: true,
          defaultValue: "",
          validationType: "string",
          validations: [
            {
              type: "required",
              params: [t("Please enter your name")],
            },
            {
              type: "min",
              params: [2, t("Your name can't be shorter than 2 characters long")],
            },
          ],
        },
        {
          type: "text",
          name: "complainantEmail",
          label: t("Email Address"),
          classes: "",
          required: true,
          defaultValue: "",
          validationType: "string",
          validations: [
            {
              type: "required",
              params: [t("Please enter your email address")],
            },
            {
              type: "email",
              params: [t("Please enter a valid email address")],
            },
          ],
        },
      ],
    },
    {
      fields: [
        {
          type: "select",
          defaultValue: "",
          defaultOption: {
            value: "",
            label: t("Please select a complaint type"),
          },
          subtext: "",
          name: "complaintType",
          label: t("Type of Complaint"),
          required: true,
          validationType: "string",
          validations: [
            {
              type: "required",
              params: [t("Please select a complaint type")],
            },
          ],
          options: [
            { label: t("Campaign page"), value: "Page" },
            { label: t("Image"), value: "Image" },
            { label: t("Comment"), value: "Comment" },
            { label: t("Other"), value: "Other" },
          ],
        },
      ],
    },
    {
      classes: ["report-campaign-reason"],
      fields: [
        {
          type: "checkboxGroup",
          name: "complaintReason",
          label: t("Please select a complaint reason"),
          options: [
            {
              label: t("The content is defamatory or libellous"),
              value: "Defamatory",
            },
            {
              label: t("The content violates copyright or trademark"),
              value: "Copyright",
            },
            {
              label: t("The content is fraudulent"),
              value: "Fraudulent",
            },
            {
              label: t("The content is or contains spam"),
              value: "Spam",
            },
            {
              label: t("Other"),
              value: "Other",
            },
          ],
          validationType: "array",
          validations: [
            {
              type: "typeError",
              params: [t("You must select at least one complaint reason")],
            },
            {
              type: "required",
              params: [t("You must select at least one complaint reason")],
            },
            {
              type: "min",
              params: [1, t("You must select at least one complaint reason")],
            },
          ],
        },
      ],
    },
    {
      fields: [
        {
          type: "text",
          name: "campaignUrl",
          subtext: campaignUrl
            ? "This was pre-filled based on the campaign you were just viewing"
            : "",
          label: t("URL of Campaign"),
          classes: "",
          required: true,
          defaultValue: campaignUrl,
          validationType: "string",
          validations: [
            {
              type: "required",
              params: [t("Please provide the campaign URL")],
            },
          ],
        },
      ],
    },
    {
      fields: [
        {
          type: "wysiwyg",
          name: "content",
          label: t("Complaint"),
          subtext: t(
            "Please describe the nature of your complaint, including any supporting evidence",
          ),
          required: true,
          validationType: "string",
          validations: [
            {
              type: "required",
              params: [t("Please add a description of your complaint")],
            },
            {
              type: "max",
              params: [1000, t("Your complaint cannot be more than 1000 characters long.")],
            },
          ],
          minHeight: 175,
        },
      ],
    },
    {
      classes: ["report-campaign-consent"],
      fields: [
        {
          type: "checkbox",
          name: "consent",
          classes: ["campaign-report-form__checkbox"],
          label: t(
            "I consent that the information provided is true and accurate to the best of my knowledge",
          ),
          defaultValue: false,
          validationType: "boolean",
          validations: [
            {
              type: "required",
              params: [t("The consent statement must be accepted")],
            },
            {
              type: "oneOf",
              params: [[true], t("The consent statement must be accepted")],
            },
          ],
        },
      ],
    },
    {
      fields: [
        {
          type: "submit",
          value: t("Submit"),
          loadingMessage: t("Please wait..."),
          classes: "",
          isSubmitting: loading,
        },
      ],
    },
  ];

  const handleSubmit = () => {
    submitReport({
      variables: {
        input: {
          complainant: {
            name: submittedFormData.complainantName,
            email: submittedFormData.complainantEmail,
          },
          details: {
            complaintType: submittedFormData.complaintType,
            campaignLink: submittedFormData.campaignUrl,
            campaignId,
            complaintReason: submittedFormData.complaintReason,
            description: submittedFormData.content,
          },
          consent: submittedFormData.consent,
          // This can just be true, as the user will have passed through verification to get here
          verified: true,
        },
      },
      context: {
        headers: {
          "x-captcha-token": captchaToken,
        },
      },
    });
  };

  const verifySubmission = (data) => {
    setSubmittedFormData(data);
    setVerificationModalOpen(true);
  };

  const onVerification = () => {
    closeVerificationModal();
    handleSubmit();
  };

  const closeVerificationModal = () => {
    setVerificationModalOpen(false);
  };

  return !submitted ? (
    <div className="report-campaign-form">
      <ReportCampaignForm formContent={formContent} handleSubmit={verifySubmission} error={error} />

      <div className={`turnstile__wrapper ${captchaToken ? "hidden" : ""}`}>
        <TurnstileWrapper
          ref={turnstileRef}
          emitNullOnReset={false}
          onChange={(token) => setCaptchaToken(token)}
          captchaKey={TURNSTILE_KEY}
          setTurnstileInteractive={setIsTurnstileInteractive}
          onError={setTurnstileError}
          action="reportCampaign"
        />
      </div>

      {turnstileError && (
        <div>{t("We are unable to verify you at this stage. Please try again later.")}</div>
      )}

      <ModalPinVerification
        onVerified={onVerification}
        onCloseModal={closeVerificationModal}
        isOpen={isVerificationModalOpen}
        email={submittedFormData?.complainantEmail}
        awaitingCaptcha={!captchaToken || isTurnstileInteractive}
      />
    </div>
  ) : (
    <div className="report-campaign-success">
      <h1>Submission Received</h1>
      <p>
        Thank you, for your submission. It has been passed on to our Support Team who may contact
        the campaigner or take other appropriate action. The campaigner will not know who reported
        them.
      </p>

      <p>
        Please note that our review process is confidential and you will not be updated on the
        outcome.
      </p>
      <p>Please refrain from reporting the same campaign multiple times.</p>
    </div>
  );
};

export { ReportCampaign };

const container = document.getElementById("js-report-campaign");
if (container) {
  render(
    <ApolloProvider client={client}>
      <ReportCampaign />
    </ApolloProvider>,
    container,
  );
}
